import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { NeuralBackground } from "./NeuralBackground";
import { RobotOrb } from "./RobotOrb";

function Counter({ to, suffix = "", decimals = 0 }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / 1400, 1);
      setV(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return (
    <span>
      {v.toFixed(decimals)}
      {suffix}
    </span>
  );
}

function Candles() {
  const bars = Array.from({ length: 26 }, (_, i) => ({
    i,
    h: Math.round(20 + Math.abs(Math.sin(i * 1.7)) * 70),
    up: Math.sin(i * 2.3) > 0,
  }));
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-56 items-end justify-center gap-2 opacity-30">
      {bars.map((b) => (
        <motion.span
          key={b.i}
          initial={{ scaleY: 0.4, opacity: 0 }}
          animate={{ scaleY: [0.6, 1, 0.75], opacity: 1 }}
          transition={{
            duration: 4 + (b.i % 5),
            repeat: Infinity,
            repeatType: "mirror",
            delay: b.i * 0.06,
          }}
          className={`w-1.5 origin-bottom rounded-sm ${b.up ? "bg-bull" : "bg-bear"}`}
          style={{ height: b.h }}
        />
      ))}
    </div>
  );
}

const stats = [
  { label: "Historical Accuracy", value: 60.4, suffix: "%+", decimals: 1 },
  { label: "Live Trades Today", value: 128, suffix: "", decimals: 0 },
  { label: "Active Signals", value: 9, suffix: "", decimals: 0 },
];

export function Hero() {
  return (
    <section
      id="top"
      className="grain scanlines relative overflow-hidden pt-32 pb-20 lg:pt-44 lg:pb-28"
    >
      <span className="aurora -top-32 -left-24 h-[26rem] w-[26rem]" />
      <span
        className="aurora top-10 right-0 h-[22rem] w-[22rem]"
        style={{ animationDelay: "-6s", opacity: 0.35 }}
      />
      <NeuralBackground dense />
      <Candles />
      <div className="relative mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:px-8">
        <div>
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-surface/60 px-3 py-1 text-[11px] tracking-[0.2em] uppercase"
          >
            <span className="live-dot" /> 9 Engines Online
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08, duration: 0.7 }}
            className="mt-6 max-w-4xl text-4xl leading-[1.05] font-semibold tracking-tight text-balance sm:text-6xl lg:text-7xl"
          >
            Multi-Asset Trades,{" "}
            <span className="text-gold-gradient">Decided by an Institutional Mind</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.7 }}
            className="mt-6 max-w-2xl text-base text-muted-foreground sm:text-lg"
          >
            AI-powered decision system using SMC, ICT, Order Flow &amp; Risk Intelligence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.26 }}
            className="mt-9 flex flex-wrap gap-3"
          >
            <a
              href="#terminal"
              className="rounded-xl px-6 py-3 text-sm font-semibold tracking-wide text-primary-foreground uppercase transition-transform hover:-translate-y-0.5"
              style={{ backgroundImage: "var(--gradient-gold)" }}
            >
              Enter Terminal
            </a>
            <a
              href="#engines"
              className="rounded-xl border border-primary/35 px-6 py-3 text-sm font-semibold tracking-wide text-primary uppercase transition-colors hover:bg-primary/10"
            >
              View Engines
            </a>
          </motion.div>

          <div className="mt-16 grid gap-4 sm:grid-cols-3">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 + i * 0.1 }}
                className="glass glass-hover rounded-2xl p-5"
              >
                <p className="font-mono text-3xl font-semibold text-gold-gradient">
                  <Counter to={s.value} suffix={s.suffix} decimals={s.decimals} />
                </p>
                <p className="mt-1 text-xs tracking-[0.16em] text-muted-foreground uppercase">
                  {s.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        <RobotOrb className="mx-auto h-64 w-64 lg:h-80 lg:w-80" />
      </div>
    </section>
  );
}
